import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type Site = {
  id: string;
  name: string;
  site_url: string;
  platform: 'wordpress' | 'xenforo' | 'workercms';
  seo_plugin: 'none' | 'yoast' | 'rankmath' | null;
  default_status: 'publish' | 'draft' | 'pending' | null;
  woocommerce: boolean;
  tags: string[];
  notes: string | null;
  last_connection_check_at: number | null;
  connection_status: 'ok' | 'auth_fail' | 'unreachable' | null;
  created_at: number;
  updated_at: number;
  workercms_remote_site_id: number | null;
  workercms_base_url: string | null;
};

export type ConnectionTestResult = {
  ok: boolean;
  message: string;
  details?: { status?: number; hint?: string; [k: string]: unknown };
};

export type CreateSiteInput = {
  name: string;
  site_url: string;
  username: string;
  app_password: string;
  seo_plugin: 'none' | 'yoast' | 'rankmath';
  default_status: 'publish' | 'draft' | 'pending';
  woocommerce: boolean;
  tags: string[];
  notes?: string;
};

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api.get<{ sites: Site[] }>('/api/app/sites'),
  });
}

export function useSite(id: string | null) {
  return useQuery({
    queryKey: ['sites', id],
    queryFn: () => api.get<{ site: Site }>(`/api/app/sites/${id}`),
    enabled: !!id,
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSiteInput) => api.post<{ site: Site }>('/api/app/sites', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useUpdateSite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateSiteInput>) => api.patch<{ site: Site }>(`/api/app/sites/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['sites', id] });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/app/sites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useTestCredentials() {
  return useMutation({
    mutationFn: (body: { site_url: string; username: string; app_password: string }) =>
      api.post<ConnectionTestResult>('/api/app/sites/test', body),
  });
}

export function useTestSite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ConnectionTestResult>(`/api/app/sites/${id}/test`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

// ---- WorkerCms (workercms.com) hooks ----

export type WorkerCmsCreds = { base_url: string; api_key: string };
export type RemoteWorkerCmsSite = {
  id: number;
  name: string;
  language: string | null;
  primary_domain: string | null;
};

export function useTestWorkerCms() {
  return useMutation({
    mutationFn: (body: WorkerCmsCreds) =>
      api.post<ConnectionTestResult>('/api/app/sites/workercms/test', body),
  });
}

export function useDiscoverWorkerCms() {
  return useMutation({
    mutationFn: (body: WorkerCmsCreds) =>
      api.post<{ sites: RemoteWorkerCmsSite[] }>('/api/app/sites/workercms/discover', body),
  });
}

export type ConnectWorkerCmsResult = {
  added: Site[];
  skipped: Array<{ remote_id: number; reason: string }>;
  total_remote: number;
};

export function useConnectWorkerCms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkerCmsCreds) =>
      api.post<ConnectWorkerCmsResult>('/api/app/sites/workercms/connect', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export type RefreshWorkerCmsResult = {
  added: number;
  updated: number;
  total_remote: number;
  orphans: Array<{ id: string; name: string; workercms_remote_site_id: number }>;
};

export function useRefreshWorkerCms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { base_url: string }) =>
      api.post<RefreshWorkerCmsResult>('/api/app/sites/workercms/refresh', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export type WpCategory = { id: number; name: string; slug: string; parent: number; count: number };
export type WpTag = { id: number; name: string; slug: string; count: number };

/** Fetch WP categories for a site. Disabled until a site id is provided so
 *  we don't hammer the WP REST until the user actually picks a target. */
export function useSiteCategories(siteId: string | null | undefined) {
  return useQuery({
    queryKey: ['sites', siteId, 'categories'],
    queryFn: () =>
      api.get<{ categories: WpCategory[] }>(`/api/app/sites/${siteId}/categories`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSiteTags(siteId: string | null | undefined) {
  return useQuery({
    queryKey: ['sites', siteId, 'tags'],
    queryFn: () => api.get<{ tags: WpTag[] }>(`/api/app/sites/${siteId}/tags`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}
